import React from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@components/atoms/Button';
import { formatForDisplay } from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';

interface DateConflict {
  subtaskName: string;
  subtaskId: string;
  conflictType: 'startDate' | 'endDate';
  subtaskDate: string;
  mainTaskDate: string;
}

interface DateConflictModalProps {
  isOpen: boolean;
  conflicts: DateConflict[];
  proposedMainTaskStartDate: string;
  proposedMainTaskEndDate: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const DateConflictModal: React.FC<DateConflictModalProps> = ({
  isOpen,
  conflicts,
  proposedMainTaskStartDate,
  proposedMainTaskEndDate,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 99999 }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {tTaskDetail.dateConflict.title}
            </h3>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {tTaskDetail.dateConflict.description}
          </p>
          
          {/* Current vs Proposed Dates */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
              {tTaskDetail.dateConflict.mainTaskDateChanges}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">{tTaskDetail.dateConflict.currentStart}</span>
                <br />
                <span className="text-gray-600 dark:text-gray-300">
                  {formatForDisplay(proposedMainTaskStartDate)}
                </span>
              </div>
              <div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">{tTaskDetail.dateConflict.currentEnd}</span>
                <br />
                <span className="text-gray-600 dark:text-gray-300">
                  {formatForDisplay(proposedMainTaskEndDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Conflicts List */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {tTaskDetail.dateConflict.conflictingSubtasks}
            </h4>
            {conflicts.map((conflict, index) => (
              <div
                key={`${conflict.subtaskId}-${conflict.conflictType}-${index}`}
                className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-4 w-4 text-red-400 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-2 flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {conflict.subtaskName}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                      {conflict.conflictType === 'startDate'
                        ? tTaskDetail.dateConflict.subtaskStartsConflict
                            .replace('{date}', formatForDisplay(conflict.subtaskDate))
                            .replace('{mainDate}', formatForDisplay(conflict.mainTaskDate))
                        : tTaskDetail.dateConflict.subtaskEndsConflict
                            .replace('{date}', formatForDisplay(conflict.subtaskDate))
                            .replace('{mainDate}', formatForDisplay(conflict.mainTaskDate))}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-4 w-4 text-amber-400 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-2">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>{tTaskDetail.dateConflict.note}</strong> {tTaskDetail.dateConflict.noteMessage}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <Button 
            variant="secondary" 
            onClick={onCancel} 
            disabled={isLoading}>
            {tTaskDetail.dateConflict.cancel}
          </Button>
          <Button 
            variant="primary" 
            onClick={onConfirm} 
            disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block mr-2"></div>
                {tTaskDetail.dateConflict.saving}
              </>
            ) : (
              tTaskDetail.dateConflict.continueAndAdjustDates
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Use Portal to render at document.body level
  return ReactDOM.createPortal(modalContent, document.body);
};